import { CHIP_DENOMS } from "../../assets/assetManifest";
import { fmtChips } from "../../lib/api";
import { AssetImage } from "../AssetImage";
import { BlindBadge } from "./BlindBadge";

// The smallest chip on this table: a tournament never puts 1s, 5s or 25s in play.
let FLOOR = 1;
export const setChipFloor = (v) => { FLOOR = Math.max(1, v || 1); };
const denoms = () => CHIP_DENOMS.filter((d) => d.value >= FLOOR);

// Greedy chip breakdown, largest first, capped so a stack never gets silly tall.
export const chipStack = (amount, max = 8) => {
  const out = [];
  let left = amount;
  const D = denoms();
  for (let i = D.length - 1; i >= 0 && out.length < max; i--) {
    const d = D[i];
    while (left >= d.value && out.length < max) { out.push(d.id); left -= d.value; }
  }
  return out.length ? out : [denoms()[0].id];
};

// Pot payout: pick the denomination that makes the stack height track the pot size (6–40 chips), remainder in smaller chips.
export const payoutStack = (amount, max = 40) => {
  const D = denoms();
  for (let i = D.length - 1; i >= 0; i--) {
    const d = D[i];
    const n = Math.floor(amount / d.value);
    if (n >= 6) {
      const main = Array(Math.min(n, max)).fill(d.id);
      const rem = amount - n * d.value;
      return [...main, ...(rem > 0 ? chipStack(rem, max - main.length) : [])].slice(0, max);
    }
  }
  return chipStack(amount, max);
};

// A stack of chips + amount, positioned at `at` (% of table). `from` = px offset it slides in from (the seat).
// `to` = px offset it sweeps out to (the pot); when set the stack animates away. `big` = pot payout: up to 40 chips in columns of 10.
export const BetChips = ({ amount, at, from, to, slow, big, testId, badge }) => {
  const perCol = big ? 10 : 8;
  const chips = big ? payoutStack(amount) : chipStack(amount, 8);
  const cols = [];
  for (let i = 0; i < chips.length; i += perCol) cols.push(chips.slice(i, i + perCol));
  const w = big ? 34 : 30;
  const style = { left: `${at[0]}%`, top: `${at[1]}%` };
  if (from) { style["--from-x"] = `${from[0]}px`; style["--from-y"] = `${from[1]}px`; }
  if (to) { style["--to-x"] = `${to[0]}px`; style["--to-y"] = `${to[1]}px`; }
  return (
    <div data-testid={testId} className={`absolute z-20 flex items-center gap-1.5 pointer-events-none ${to ? (slow ? "chip-payout" : "chip-sweep") : "chip-push"}`} style={style}>
      <div className="flex items-end gap-0.5">
        {cols.map((col, ci) => (
          <div key={ci} className="relative" style={{ width: w, height: w + (col.length - 1) * 3 }}>
            {col.map((id, i) => <AssetImage key={i} id={id} width={w} className="absolute left-0 drop-shadow-[0_2px_2px_rgba(0,0,0,0.6)]" style={{ bottom: i * 3 }} />)}
          </div>
        ))}
      </div>
      {badge === "↩" && <span data-testid={`${testId}-badge`} className="text-[10px] font-display tracking-wider bg-emerald-600/90 text-white rounded-full px-2 py-0.5">RETURNED</span>}
      {badge && badge !== "↩" && <BlindBadge kind={badge} size={22} testId={`${testId}-badge`} />}
      <span className={`font-mono bg-slate-900/85 border border-amber-500/40 rounded-full px-2 py-0.5 text-amber-200 whitespace-nowrap ${big ? "text-sm" : "text-xs"}`}>{fmtChips(amount)}</span>
    </div>
  );
};

// Chips already collected into the pot (previous streets): exactly the chips that were wagered, stacked as they arrive, until the payout sweeps them away.
export const PotChips = ({ amount, chips }) => {
  const cols = [];
  for (let i = 0; i < chips.length; i += 8) cols.push(chips.slice(i, i + 8));
  return (
    <div data-testid="pot-chips" data-amount={amount} className="absolute z-10 flex items-end gap-0.5 pointer-events-none pot-pile" style={{ left: "calc(50% - 72px)", top: "32%", transform: "translate(-100%, -50%)" }}>
      {cols.map((col, ci) => (
        <div key={ci} className="relative" style={{ width: 30, height: 30 + (col.length - 1) * 3 }}>
          {col.map((id, i) => <AssetImage key={i} id={id} width={30} className="absolute left-0 drop-shadow-[0_2px_2px_rgba(0,0,0,0.6)]" style={{ bottom: i * 3 }} />)}
        </div>
      ))}
    </div>
  );
};
